import { useState, type FormEvent } from 'react';
import { useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PublicTermination } from '@kontor/shared';
import { api, ApiError } from '../../api';
import '../offers/offers.css';
import './contracts.css';

const stamp = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Berlin' });
const day = (iso: string) => new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${iso}T12:00:00Z`));
const message = (e: unknown, field?: string) =>
  e instanceof ApiError ? (e.status === 429 ? 'Zu viele Versuche. Bitte warten Sie einen Moment.' : ((field && e.fields[field]) || Object.values(e.fields)[0] || e.message)) : 'Das hat nicht geklappt. Bitte versuchen Sie es noch einmal.';

/**
 * Öffentliche Seite zur Bestätigung des Empfangs einer elektronischen Kündigung. Der Empfänger liest das Schreiben und bestätigt mit einem Code per E-Mail, dass es ihm zugegangen ist.
 * Die Seite sagt ausdrücklich, dass das keine Zustimmung ist.
 */
export function PublicTerminationPage() {
  const { token = '' } = useParams();
  const client = useQueryClient();
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [consent, setConsent] = useState(false);
  const [codeSent, setCodeSent] = useState<{ sentTo: string; minutes: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const t = useQuery({ queryKey: ['public-termination', token], queryFn: async () => (await api<{ data: PublicTermination }>('GET', `/public/terminations/${token}`)).data, retry: false });
  const requestCode = useMutation({
    mutationFn: async () => (await api<{ data: { sentTo: string; minutes: number } }>('POST', `/public/terminations/${token}/code`, { name: name.trim() })).data,
    onSuccess: (r) => {
      setCodeSent(r);
      setError(null);
    },
    onError: (e) => setError(message(e, 'name')),
  });
  const acknowledge = useMutation({
    mutationFn: async () => (await api<{ data: PublicTermination }>('POST', `/public/terminations/${token}/acknowledge`, { name: name.trim(), code: code.trim(), consent })).data,
    onSuccess: (r) => client.setQueryData(['public-termination', token], r),
    onError: (e) => setError(message(e, 'code')),
  });

  if (t.isLoading) return <div className="center-screen">Wird geladen …</div>;
  if (t.isError || !t.data) {
    return (
      <div className="center-screen">
        <div className="card narrow">
          <h1>Kündigung nicht verfügbar</h1>
          <p className="muted">Der Link ist ungültig oder wurde zurückgezogen. Bitte wenden Sie sich an das Unternehmen, das Ihnen die Kündigung geschickt hat.</p>
        </div>
      </div>
    );
  }
  const k = t.data;

  const request = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (name.trim().length < 2) return setError('Bitte geben Sie Ihren Namen an.');
    requestCode.mutate();
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!/^\d{6}$/.test(code.trim())) return setError('Der Code besteht aus sechs Ziffern.');
    if (!consent) return setError('Bitte bestätigen Sie die Erklärung mit dem Häkchen.');
    acknowledge.mutate();
  };

  return (
    <div className="public-offer">
      <header className="public-offer__head"><strong>{k.companyName}</strong></header>
      <main className="public-offer__main">
        <div className="card">
          <div className="muted small">Kündigung · {k.number}</div>
          <h1>Kündigung des Vertrags „{k.contractTitle}“</h1>
          <p className="muted small">für {k.partyName}</p>
          <pre className="contract-body contract-body--box">{k.noticeText}</pre>
          <p><a className="btn btn--secondary" href={`/api/v1/public/terminations/${token}/pdf`} target="_blank" rel="noreferrer">Schreiben als PDF herunterladen</a></p>
        </div>

        {k.status === 'delivered' && (
          <div className="alert alert--ok" role="status">
            Der Empfang ist{k.deliveredAt ? ` am ${stamp.format(new Date(k.deliveredAt))}` : ''} bestätigt.{k.effectiveDate ? ` Der Vertrag endet am ${day(k.effectiveDate)}.` : ''} Das ist keine Zustimmung zur Kündigung.
          </div>
        )}
        {k.status === 'withdrawn' && <div className="alert alert--warn" role="status">Diese Kündigung wurde zurückgezogen.</div>}

        {k.status === 'issued' && (
          <div className="card">
            <h2>Empfang bestätigen</h2>
            <p className="muted">Mit dem Code bestätigen Sie nur, dass Ihnen dieses Schreiben zugegangen ist. Es ist keine Zustimmung zur Kündigung, und Sie geben keine Rechte auf.</p>
            {error && <div className="alert alert--error" role="alert">{error}</div>}
            {!codeSent ? (
              <form className="contract-sign" onSubmit={request} noValidate>
                <label className="field">
                  <span>Ihr vollständiger Name</span>
                  <input className="input" value={name} autoComplete="name" maxLength={160} onChange={(e) => setName(e.target.value)} />
                </label>
                <div className="form-actions">
                  <button className="btn btn--primary" type="submit" disabled={requestCode.isPending}>{requestCode.isPending ? 'Wird gesendet …' : 'Code an meine E-Mail senden'}</button>
                </div>
              </form>
            ) : (
              <form className="contract-sign" onSubmit={submit} noValidate>
                <p>Wir haben einen Code an <strong>{codeSent.sentTo}</strong> gesendet. Er gilt {codeSent.minutes} Minuten.</p>
                <label className="field">
                  <span>Code aus der E-Mail</span>
                  <input className="input contract-code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} />
                </label>
                <label className="check">
                  <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
                  <span>{k.statement}</span>
                </label>
                <div className="form-actions">
                  <button className="btn btn--primary" type="submit" disabled={acknowledge.isPending}>{acknowledge.isPending ? 'Wird bestätigt …' : 'Empfang bestätigen'}</button>
                  <button className="btn btn--secondary" type="button" disabled={requestCode.isPending} onClick={() => requestCode.mutate()}>Neuen Code senden</button>
                </div>
              </form>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
